import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Clipboard, ClipboardCheck, Sparkles, Timer } from 'lucide-react';
import { toast } from 'sonner';
import { ProviderApiTokenCreateInput, ProviderApiTokenCreateResponse } from '@/types/provider-api-token';
import { analyticsService } from '@/services/analytics.service';
import { cn } from '@/lib/utils';

const createTokenSchema = z.object({
  label: z
    .string()
    .min(3, 'settings.providerApi.tokenForm.labelTooShort')
    .max(120, 'settings.providerApi.tokenForm.labelTooLong'),
  scopes: z
    .array(z.string())
    .min(1, 'settings.providerApi.tokenForm.scopesRequired'),
  expiresInMinutes: z.number().int().positive().nullable().optional(),
  notes: z.string().max(240).optional(),
});

type CreateTokenFormValues = z.infer<typeof createTokenSchema>;

interface AvailableScope {
  id: string;
  label: string;
  description: string;
}

interface CreateProviderApiTokenDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  availableScopes: ReadonlyArray<AvailableScope>;
  isCreating: boolean;
  createToken: (_input: ProviderApiTokenCreateInput) => Promise<ProviderApiTokenCreateResponse>;
  onTokenIssued: (_response: ProviderApiTokenCreateResponse) => void;
}

const EXPIRY_OPTIONS: Array<{ labelKey: string; value: number | null }> = [
  { labelKey: 'settings.providerApi.tokenForm.expiry.never', value: null },
  { labelKey: 'settings.providerApi.tokenForm.expiry.oneHour', value: 60 },
  { labelKey: 'settings.providerApi.tokenForm.expiry.oneDay', value: 60 * 24 },
  { labelKey: 'settings.providerApi.tokenForm.expiry.oneWeek', value: 60 * 24 * 7 },
  { labelKey: 'settings.providerApi.tokenForm.expiry.thirtyDays', value: 60 * 24 * 30 },
];

export function CreateProviderApiTokenDialog({
  open,
  onOpenChange,
  availableScopes,
  isCreating,
  createToken,
  onTokenIssued,
}: CreateProviderApiTokenDialogProps) {
  const { t } = useTranslation();
  const [issuedToken, setIssuedToken] = useState<ProviderApiTokenCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const form = useForm<CreateTokenFormValues>({
    resolver: zodResolver(createTokenSchema),
    defaultValues: {
      label: '',
      scopes: availableScopes.length > 0 ? [availableScopes[0].id] : [],
      expiresInMinutes: null,
      notes: '',
    },
  });

  useEffect(() => {
    if (!open) {
      setIssuedToken(null);
      setCopied(false);
      form.reset({
        label: '',
        scopes: availableScopes.length > 0 ? [availableScopes[0].id] : [],
        expiresInMinutes: null,
        notes: '',
      });
    }
  }, [open, form, availableScopes]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const payload: ProviderApiTokenCreateInput = {
        label: values.label.trim(),
        scopes: values.scopes,
      };

      if (typeof values.expiresInMinutes === 'number') {
        payload.expiresInMinutes = values.expiresInMinutes;
      }

      const response = await createToken(payload);
      setIssuedToken(response);
      onTokenIssued(response);

      analyticsService.track('provider_api_token_labelled', {
        labelLength: payload.label.length,
        scopes: payload.scopes,
        expiresInMinutes: payload.expiresInMinutes ?? null,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('settings.providerApi.tokenForm.creationError', 'Unable to issue token.');
      toast.error(message);
    }
  });

  const handleCopy = async () => {
    if (!issuedToken) {
      return;
    }

    const copyWithExecCommand = () => {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = issuedToken.token;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!successful) {
          throw new Error('execCommand failed');
        }
        return true;
      } catch {
        return false;
      }
    };

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(issuedToken.token);
      } else if (!copyWithExecCommand()) {
        throw new Error('Clipboard API unavailable');
      }
      setCopied(true);
      toast.success(t('settings.providerApi.tokenForm.copied', 'Token copied to clipboard.'));
      analyticsService.track('provider_api_token_copied', { tokenId: issuedToken.tokenId });
    } catch (error) {
      console.error('Token copy failed', error);
      if (copyWithExecCommand()) {
        setCopied(true);
        toast.success(t('settings.providerApi.tokenForm.copied', 'Token copied to clipboard.'));
        analyticsService.track('provider_api_token_copied', { tokenId: issuedToken.tokenId });
        return;
      }
      toast.error(t('settings.providerApi.tokenForm.copyFailed', 'Unable to copy token.'));
    }
  };

  const tokenDescription = useMemo(() => {
    if (!issuedToken?.expiresAt) {
      return t('settings.providerApi.tokenForm.resultNoExpiry', 'This token never expires automatically.');
    }
    return t('settings.providerApi.tokenForm.resultExpiry', 'Expires on {{date}}', {
      date: new Date(issuedToken.expiresAt).toLocaleString(),
    });
  }, [issuedToken, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('settings.providerApi.tokenForm.title', 'Generate service token')}</DialogTitle>
          <DialogDescription>
            {t(
              'settings.providerApi.tokenForm.subtitle',
              'One final copy of the token will appear after creation. Keep it in a secure password manager.',
            )}
          </DialogDescription>
        </DialogHeader>

        {issuedToken ? (
          <div className="space-y-6">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                {t(
                  'settings.providerApi.tokenForm.resultNotice',
                  'Copy this value now. You will not be able to view it again after closing this dialog.',
                )}
              </AlertDescription>
            </Alert>

            <div className="rounded-lg border bg-muted/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium">{issuedToken.token}</p>
                  <p className="text-xs text-muted-foreground">{tokenDescription}</p>
                </div>
                <Button onClick={handleCopy} variant="outline" className="gap-2">
                  {copied ? <ClipboardCheck className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                  {copied
                    ? t('settings.providerApi.tokenForm.copiedAction', 'Copied')
                    : t('settings.providerApi.tokenForm.copyAction', 'Copy token')}
                </Button>
              </div>
            </div>

            <DialogFooter className="justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {t('common.done', 'Done')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings.providerApi.tokenForm.label', 'Token label')}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('settings.providerApi.tokenForm.labelPlaceholder', 'e.g., Make Zapier sync')} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="scopes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings.providerApi.tokenForm.scopes', 'Scopes')}</FormLabel>
                      <div className="space-y-3 rounded-lg border p-4">
                        {availableScopes.map((scope) => {
                          const selected = field.value?.includes(scope.id);
                          return (
                            <label
                              key={scope.id}
                              className={cn(
                                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition',
                                selected
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50',
                              )}
                            >
                              <Checkbox
                                checked={selected}
                                onCheckedChange={(checked) => {
                                  const current = new Set(field.value ?? []);
                                  if (checked) {
                                    current.add(scope.id);
                                  } else {
                                    current.delete(scope.id);
                                  }
                                  field.onChange(Array.from(current));
                                }}
                              />
                              <span>
                                <span className="flex items-center gap-2 font-medium">{scope.label}</span>
                                <span className="block text-sm text-muted-foreground">{scope.description}</span>
                                <Badge variant="outline" className="mt-2 text-xs">
                                  {scope.id}
                                </Badge>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="expiresInMinutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings.providerApi.tokenForm.expiry.label', 'Expiry')}</FormLabel>
                      <FormControl>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {EXPIRY_OPTIONS.map((option) => {
                            const isSelected =
                              option.value === null
                                ? field.value === null || field.value === undefined
                                : field.value === option.value;
                            return (
                              <button
                                key={option.labelKey}
                                type="button"
                                className={cn(
                                  'flex items-center gap-2 rounded-lg border p-3 text-left text-sm transition',
                                  isSelected
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border hover:border-primary/50',
                                )}
                                onClick={() => field.onChange(option.value)}
                              >
                                <Timer className="h-4 w-4" />
                                <span>{t(option.labelKey)}</span>
                              </button>
                            );
                          })}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('settings.providerApi.tokenForm.notes', 'Internal note')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t(
                            'settings.providerApi.tokenForm.notesPlaceholder',
                            'Optional. Document where this token is stored or which integration will use it.',
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={isCreating}>
                  {isCreating
                    ? t('settings.providerApi.tokenForm.creating', 'Issuing token…')
                    : t('settings.providerApi.tokenForm.submit', 'Create token')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
